import { useState, useEffect } from 'react';
import { Plus, CheckCircle, XCircle, Gift } from 'lucide-react';
import {
  getAllPromotionCodes,
  createPromotionCode,
  deactivatePromotionCode,
  activatePromotionCode,
  type PromotionCode,
  type CreatePromotionCodeDto
} from '../../../services/promotionCodesService';

export default function CouponsPage() {
  const [promotionCodes, setPromotionCodes] = useState<PromotionCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<CreatePromotionCodeDto>({
    code: '',
    isManaged: true,
    discountType: 'Percentage',
    discountValue: 0,
    trialPeriodDays: undefined,
    durationInMonths: undefined,
    maxRedemptions: undefined,
    expiresAt: undefined
  });

  useEffect(() => {
    loadPromotionCodes();
  }, []);

  const loadPromotionCodes = async () => {
    try {
      setLoading(true);
      const data = await getAllPromotionCodes();
      setPromotionCodes(data);
    } catch (error) {
      console.error('Error loading promotion codes:', error);
      alert('Erro ao carregar cupons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Converter TrialDays para formato correto do backend
      let payload: CreatePromotionCodeDto = { ...formData };
      
      if (formData.discountType === 'TrialDays') {
        // Cupons com apenas trial period devem ser managed
        payload = {
          ...formData,
          isManaged: true,
          discountType: 'Percentage',
          discountValue: 0,
          trialPeriodDays: formData.discountValue > 0 ? formData.discountValue : undefined
        };
      }
      
      await createPromotionCode(payload);
      alert('Cupom criado com sucesso!');
      setShowModal(false);
      resetForm();
      loadPromotionCodes();
    } catch (error: any) {
      console.error('Error creating promotion code:', error);
      alert(error.response?.data?.error || 'Erro ao criar cupom');
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Deseja desativar este cupom?')) return;
    
    try {
      await deactivatePromotionCode(id);
      alert('Cupom desativado com sucesso!');
      loadPromotionCodes();
    } catch (error) {
      console.error('Error deactivating promotion code:', error);
      alert('Erro ao desativar cupom');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await activatePromotionCode(id);
      alert('Cupom ativado com sucesso!');
      loadPromotionCodes();
    } catch (error) {
      console.error('Error activating promotion code:', error);
      alert('Erro ao ativar cupom');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      isManaged: true,
      discountType: 'Percentage',
      discountValue: 0,
      trialPeriodDays: undefined,
      durationInMonths: undefined,
      maxRedemptions: undefined,
      expiresAt: undefined
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getDurationText = (months?: number) => {
    if (!months) return 'Vitalício';
    if (months === 1) return 'Uma vez';
    return `${months} meses`;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Cupons de Desconto</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Criar Cupom
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Desconto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duração
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expira
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promotionCodes.map((code) => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Gift className={`mr-2 ${code.isComplementary ? 'text-purple-600' : 'text-green-600'}`} size={20} />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{code.code}</div>
                        <div className="text-xs text-gray-500">
                          {code.isManaged ? 'Gerenciado' : 'Stripe'}
                          {code.isComplementary && ' • Vitalício'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {code.discountType === 'Percentage' && 'Porcentagem'}
                    {code.discountType === 'FixedAmount' && 'Valor Fixo'}
                    {code.discountType === 'TrialDays' && 'Trial'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {code.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getDurationText(code.durationInMonths)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {code.timesRedeemed} {code.maxRedemptions ? `/ ${code.maxRedemptions}` : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(code.expiresAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      code.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {code.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {code.isActive ? (
                      <button
                        onClick={() => handleDeactivate(code.id)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                      >
                        <XCircle size={16} />
                        Desativar
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivate(code.id)}
                        className="text-green-600 hover:text-green-900 flex items-center gap-1"
                      >
                        <CheckCircle size={16} />
                        Ativar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {promotionCodes.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Nenhum cupom cadastrado
            </div>
          )}
        </div>
      )}

      {/* Modal de Criação */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Criar Novo Cupom</h2>
            
            <form onSubmit={handleSubmit}>
              {/* Código */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="BEMVINDO50"
                  required
                />
              </div>

              {/* Tipo de Gerenciamento */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Gerenciamento *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.isManaged}
                      onChange={() => setFormData({ ...formData, isManaged: true })}
                      className="mr-2"
                    />
                    Gerenciado (sem pagamento)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!formData.isManaged}
                      onChange={() => setFormData({ ...formData, isManaged: false })}
                      className="mr-2"
                    />
                    Stripe (com pagamento)
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.isManaged 
                    ? 'Cupom 100% OFF - Ideal para influencers e parcerias'
                    : 'Cupom gerenciado pelo Stripe com desconto parcial'
                  }
                </p>
              </div>

              {/* Tipo de Desconto */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Desconto *
                </label>
                <select
                  value={formData.discountType}
                  onChange={(e) => setFormData({ ...formData, discountType: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="Percentage">Porcentagem</option>
                  <option value="FixedAmount" disabled={formData.isManaged}>Valor Fixo (R$)</option>
                  <option value="TrialDays" disabled={formData.isManaged}>Dias Gratuitos</option>
                </select>
              </div>

              {/* Valor do Desconto / Dias Gratuitos */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.discountType === 'TrialDays' ? 'Dias Gratuitos *' : 'Valor do Desconto *'}
                </label>
                <input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={formData.discountType === 'Percentage' ? '50' : formData.discountType === 'TrialDays' ? '30' : '10.00'}
                  min="0"
                  max={formData.discountType === 'Percentage' ? '100' : undefined}
                  step={formData.discountType === 'FixedAmount' ? '0.01' : '1'}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.discountType === 'Percentage' && 'Porcentagem de desconto (0-100)'}
                  {formData.discountType === 'FixedAmount' && 'Valor em reais'}
                  {formData.discountType === 'TrialDays' && 'Número de dias gratuitos antes da primeira cobrança. Cupom será criado como gerenciado (managed).'}
                </p>
              </div>

              {/* Duração */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duração
                </label>
                <select
                  value={formData.durationInMonths === undefined ? 'forever' : formData.durationInMonths === 1 ? 'once' : 'custom'}
                  onChange={(e) => {
                    if (e.target.value === 'forever') {
                      setFormData({ ...formData, durationInMonths: undefined });
                    } else if (e.target.value === 'once') {
                      setFormData({ ...formData, durationInMonths: 1 });
                    } else {
                      setFormData({ ...formData, durationInMonths: 3 });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={formData.isManaged}
                >
                  <option value="forever">Vitalício</option>
                  <option value="once">Uma vez</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>

              {formData.durationInMonths !== undefined && formData.durationInMonths !== 1 && !formData.isManaged && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Meses
                  </label>
                  <input
                    type="number"
                    value={formData.durationInMonths || 3}
                    onChange={(e) => setFormData({ ...formData, durationInMonths: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    min="2"
                  />
                </div>
              )}

              {/* Máximo de Resgates */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Máximo de Resgates (opcional)
                </label>
                <input
                  type="number"
                  value={formData.maxRedemptions || ''}
                  onChange={(e) => setFormData({ ...formData, maxRedemptions: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Deixe vazio para ilimitado"
                  min="1"
                />
              </div>

              {/* Data de Expiração */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Expiração (opcional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt || ''}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Criar Cupom
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}



